import { useEffect, useRef, useState } from 'react';
// The CSP MapLibre build with a real worker file — see CxRegisterMap.jsx for the
// full reason; the short version is that Create React App's minifier breaks
// MapLibre's default inline-blob worker, so we load the worker from a file
// webpack never touches.
import maplibregl from 'maplibre-gl/dist/maplibre-gl-csp';
import { Protocol } from 'pmtiles';
import 'maplibre-gl/dist/maplibre-gl.css';

maplibregl.setWorkerUrl('/maplibre-gl-csp-worker.js');

const TILES = '/tiles/uk.pmtiles';

// Same UK basemap archive the CX register map uses, served by our own nginx.
const protocol = new Protocol();
maplibregl.addProtocol('pmtiles', protocol.tilev4);

// Pins are coloured by the climb verdict, so the map answers the safety
// question at a glance: red poles need a hoist, amber want assessing, green are
// climbable.
const VERDICT_COLOUR = { hoist: '#dc2626', caution: '#d97706', climb: '#059669' };
const SEARCH = '#711C91'; // the searched location itself

// Quiet basemap so the pins are the subject — copied from the CX register map,
// which is the version proven to render.
const style = {
  version: 8,
  sources: {
    basemap: {
      type: 'vector',
      url: `pmtiles://${window.location.origin}${TILES}`,
      attribution: 'OpenStreetMap · Protomaps',
    },
  },
  layers: [
    { id: 'bg', type: 'background', paint: { 'background-color': '#b9cbd9' } },
    {
      id: 'earth', type: 'fill', source: 'basemap', 'source-layer': 'earth', paint: { 'fill-color': '#ece8e1' },
    },
    {
      id: 'landuse',
      type: 'fill',
      source: 'basemap',
      'source-layer': 'landuse',
      filter: ['in', ['get', 'kind'], ['literal', ['park', 'forest', 'wood', 'nature_reserve', 'grass', 'farmland', 'scrub']]],
      paint: { 'fill-color': '#dde5d4' },
    },
    {
      id: 'water', type: 'fill', source: 'basemap', 'source-layer': 'water', paint: { 'fill-color': '#b9cbd9' },
    },
    {
      id: 'buildings', type: 'fill', source: 'basemap', 'source-layer': 'buildings', minzoom: 13, paint: { 'fill-color': '#ddd8d0' },
    },
    {
      id: 'roads-minor',
      type: 'line',
      source: 'basemap',
      'source-layer': 'roads',
      minzoom: 11,
      filter: ['in', ['get', 'kind'], ['literal', ['minor_road', 'medium_road']]],
      paint: { 'line-color': '#d8d1c8', 'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 16, 4] },
    },
    {
      id: 'roads-major',
      type: 'line',
      source: 'basemap',
      'source-layer': 'roads',
      filter: ['in', ['get', 'kind'], ['literal', ['highway', 'major_road']]],
      paint: { 'line-color': '#c9c0b6', 'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.6, 10, 2, 16, 7] },
    },
    {
      id: 'boundaries',
      type: 'line',
      source: 'basemap',
      'source-layer': 'boundaries',
      paint: { 'line-color': '#a99fb0', 'line-dasharray': [2, 2], 'line-width': 0.9 },
    },
  ],
};

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

// Straight-line ground distance between two lon/lat points, in metres. This is
// as-the-crow-flies — a real dropwire runs longer — so it is a planning figure,
// which is exactly what a desktop survey wants.
function metresBetween([lon1, lat1], [lon2, lat2]) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

export default function PoleSurveyMap({ poles = [], centre, onSelect, height = 460 }) {
  const holder = useRef(null);
  const map = useRef(null);
  const ready = useRef(false);
  // The pole picked as the measuring origin: [lon, lat] + a label.
  const measureFrom = useRef(null);
  const houseMarker = useRef(null);
  const distancePopup = useRef(null);
  const setHint = useRef(null); // set inside effect; updates the on-map hint
  const [hint, setHintState] = useState('');
  setHint.current = setHintState;

  // Reset any in-progress measurement (new search, or "clear").
  const clearMeasure = (m) => {
    measureFrom.current = null;
    if (houseMarker.current) { houseMarker.current.remove(); houseMarker.current = null; }
    if (distancePopup.current) { distancePopup.current.remove(); distancePopup.current = null; }
    const src = m && m.getSource('measure');
    if (src) src.setData({ type: 'FeatureCollection', features: [] });
    if (setHint.current) setHint.current('');
  };

  useEffect(() => {
    if (map.current || !holder.current) return undefined;
    const m = new maplibregl.Map({
      container: holder.current, style, center: [-2.4, 53.2], zoom: 5.4, attributionControl: false,
    });
    map.current = m;
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    m.addControl(new maplibregl.AttributionControl({ compact: true, customAttribution: 'OpenStreetMap · Protomaps · Openreach' }), 'bottom-right');
    m.on('error', (e) => console.error('[pole-map]', (e && e.error && e.error.message) || e));

    m.on('load', () => {
      m.addSource('poles', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addSource('centre', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addSource('measure', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

      // The measuring line: pole → the point clicked.
      m.addLayer({
        id: 'measure-line',
        type: 'line',
        source: 'measure',
        paint: { 'line-color': '#111827', 'line-width': 2, 'line-dasharray': [2, 1.5] },
      });

      // The searched location — a hollow ring, so it reads as "here" without
      // being mistaken for a pole.
      m.addLayer({
        id: 'search-centre',
        type: 'circle',
        source: 'centre',
        paint: {
          'circle-radius': 9, 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-width': 3, 'circle-stroke-color': SEARCH,
        },
      });

      m.addLayer({
        id: 'pole-dots',
        type: 'circle',
        source: 'poles',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 5, 17, 9],
          'circle-color': ['match', ['get', 'verdict'],
            'hoist', VERDICT_COLOUR.hoist, 'caution', VERDICT_COLOUR.caution, VERDICT_COLOUR.climb],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
        },
      });

      ready.current = true;
      m.fire('poles.ready');
    });

    m.on('mouseenter', 'pole-dots', () => { m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', 'pole-dots', () => { m.getCanvas().style.cursor = ''; });

    // Click a pole: show its detail AND set it as the measuring origin. The next
    // click anywhere drops a marker and measures the straight-line distance.
    m.on('click', 'pole-dots', (e) => {
      const f = e.features && e.features[0];
      if (!f) return;
      const p = f.properties;
      const coords = f.geometry.coordinates.slice();
      new maplibregl.Popup({ offset: 12, maxWidth: '260px' })
        .setLngLat(coords)
        .setHTML(`<div style="font:400 12px system-ui,sans-serif;line-height:1.45">
            <strong>DP ${escapeHtml(p.dp)}/${escapeHtml(p.pole)}</strong>${p.metres ? ` · ${escapeHtml(p.metres)}m` : ''}<br>
            <span style="color:${VERDICT_COLOUR[p.verdict] || '#333'};font-weight:600">${escapeHtml(p.verdictLabel)}</span><br>
            <span style="color:#6b7280">${escapeHtml(p.result || '')}</span>${p.address ? `<br><span style="color:#6b7280">${escapeHtml(p.address)}</span>` : ''}
          </div>`)
        .addTo(m);
      if (onSelect) onSelect(Number(p.uid));

      // Start a measurement from this pole.
      clearMeasure(m);
      measureFrom.current = { coords, label: `DP ${p.dp}/${p.pole}` };
      setHintState('Now click the house or point to measure the distance.');
    });

    // A click NOT on a pole, while a pole is armed, is the "to" point: draw the
    // line and label the distance.
    m.on('click', (e) => {
      if (!measureFrom.current) return;
      const onPole = m.queryRenderedFeatures(e.point, { layers: ['pole-dots'] });
      if (onPole.length) return; // handled by the pole handler above
      const to = [e.lngLat.lng, e.lngLat.lat];
      const from = measureFrom.current.coords;
      const metres = metresBetween(from, to);

      m.getSource('measure').setData({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates: [from, to] }, properties: {} }],
      });
      if (houseMarker.current) houseMarker.current.remove();
      const el = document.createElement('div');
      el.style.cssText = 'width:14px;height:14px;border-radius:2px;background:#111827;border:2px solid #fff;box-shadow:0 0 0 1px #999';
      houseMarker.current = new maplibregl.Marker({ element: el }).setLngLat(to).addTo(m);

      if (distancePopup.current) distancePopup.current.remove();
      const mid = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];
      distancePopup.current = new maplibregl.Popup({ closeButton: false, offset: 8 })
        .setLngLat(mid)
        .setHTML(`<div style="font:600 13px system-ui,sans-serif">${metres} m<br><span style="font-weight:400;color:#6b7280;font-size:11px">from ${escapeHtml(measureFrom.current.label)} · straight line</span></div>`)
        .addTo(m);
      setHintState(`${metres} m from ${measureFrom.current.label} — click another pole to measure again.`);
    });

    return () => { m.remove(); map.current = null; ready.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update pins + centre and fit to them whenever a new search comes back.
  useEffect(() => {
    const m = map.current;
    if (!m) return undefined;
    const apply = () => {
      clearMeasure(m); // a new search resets any measurement
      const pins = poles.filter((p) => p.latitude != null && p.longitude != null);
      m.getSource('poles')?.setData({
        type: 'FeatureCollection',
        features: pins.map((p) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [Number(p.longitude), Number(p.latitude)] },
          properties: {
            uid: p.uid,
            dp: p.dp,
            pole: p.pole,
            verdict: p.verdict,
            verdictLabel: { hoist: 'Hoist required', caution: 'Assess on site', climb: 'Climbable' }[p.verdict] || p.verdict,
            result: p.result_description || p.result || '',
            address: p.address || '',
            metres: p.metres != null ? p.metres : '',
          },
        })),
      });

      m.getSource('centre')?.setData(centre
        ? { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: [centre.lon, centre.lat] }, properties: {} }] }
        : { type: 'FeatureCollection', features: [] });

      const pts = pins.map((p) => [Number(p.longitude), Number(p.latitude)]);
      if (centre) pts.push([centre.lon, centre.lat]);
      if (pts.length === 1) {
        m.easeTo({ center: pts[0], zoom: 16, duration: 400 });
      } else if (pts.length > 1) {
        const b = new maplibregl.LngLatBounds();
        pts.forEach((c) => b.extend(c));
        m.fitBounds(b, { padding: 60, maxZoom: 17, duration: 400 });
      }
    };
    if (ready.current) apply(); else m.once('poles.ready', apply);
    return undefined;
  }, [poles, centre]);

  return (
    <div>
      <div className="relative">
        <div ref={holder} style={{ height }} className="w-full overflow-hidden rounded-lg border border-gray-200" />
        {/* Measurement hint / result, floated over the map. */}
        <div className="pointer-events-none absolute left-2 top-2 flex items-center gap-2">
          <span className="pointer-events-auto rounded-md bg-white/95 px-2.5 py-1 text-xs text-gray-700 shadow-sm ring-1 ring-gray-200">
            {hint || 'Click a pole to see its status, then click a point to measure the distance.'}
          </span>
          {hint ? (
            <button
              type="button"
              onClick={() => clearMeasure(map.current)}
              className="pointer-events-auto rounded-md bg-white/95 px-2 py-1 text-xs font-medium text-brand shadow-sm ring-1 ring-gray-200 hover:bg-white"
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-600">
        {[['climb', 'Climbable'], ['caution', 'Assess on site'], ['hoist', 'Hoist required']].map(([k, label]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: VERDICT_COLOUR[k], border: '1.5px solid #fff', boxShadow: '0 0 0 1px #ccc' }} />
            {label}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full" style={{ border: `2px solid ${SEARCH}` }} />
          Searched location
        </span>
      </div>
    </div>
  );
}
